(ns minorodata.components.dependency-graph.timeline
  "Execution-timeline view (gantt of runs + executions table) shown in a bottom
  pane on the dependency graph. Moved out of the pipeline editor; state lives in
  plain re-frame db (see `minorodata.dependency-graph.timeline.{events,subs,api}`).

  The pane covers every transform in the *pipeline* the opened one belongs to
  (kioku answers with the whole connected component), which is why the host passes
  `master->name`: the API's rows carry ids only."
  (:require
   ["allotment" :refer [Allotment]]
   ["date-fns" :refer [differenceInMilliseconds endOfDay formatISO
                       isAfter isBefore isEqual parseISO startOfDay]]
   ["date-fns-tz" :refer [formatInTimeZone fromZonedTime toZonedTime]]
   ["lucide-react" :refer [Activity ChevronLeftIcon ChevronRightIcon Copy
                           Crosshair EllipsisVertical FunnelIcon FunnelXIcon
                           Info RefreshCcw ScrollText X ZoomInIcon ZoomOutIcon]]
   ["react" :as react]
   [clojure.string :as str]
   [goog.functions :refer [debounce]]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.utils :refer [format-execution-time short-duration]]
   [minorodata.dependency-graph.timeline.events :as timeline.events]
   [minorodata.dependency-graph.timeline.window :refer [hours-ending-now run-window run-summary safe-format
                                                        same-day? valid-date? window-text]]
   [re-frame.core :as re-frame]
   [ui.alert :refer [alert]]
   [ui.button :refer [button]]
   [ui.date-time :refer [date-tooltip get-browser-timezone]]
   [ui.datepicker :refer [datetime-picker]]
   [ui.dropdown-menu :as dropdown-menu]
   [ui.gantt :refer [content-height gantt]]
   [ui.popover :as popover]
   [ui.skeleton :refer [skeleton]]
   [ui.status :as status]
   [ui.status-vocab :refer [normalize-status]]
   [ui.table :as table]
   [ui.table-widths :refer [column-widths]]
   [ui.tooltip :refer [tooltip]]
   [ui.utils :refer [clsx]]))


(defn window-key
  "Value-identity for a window, for `useEffect` deps: React compares those with
  `Object.is`, which a re-derived CLJS map fails even when its value is equal."
  [{:keys [started-at ended-at]}]
  (when (and started-at ended-at)
    (str (formatISO started-at) "/" (formatISO ended-at))))

(defn history-key
  "Identity of the request the gantt endpoint receives: it takes whole UTC dates,
  so panning inside a day is the same request."
  [timezone {:keys [started-at ended-at]}]
  (when (and started-at ended-at timezone)
    (str (formatInTimeZone (fromZonedTime started-at timezone) "UTC" "yyyy-MM-dd")
         "/"
         (formatInTimeZone (fromZonedTime ended-at timezone) "UTC" "yyyy-MM-dd"))))

(defn set-window!
  "Writes an absolute window to the db, ignoring a half-built one — the gantt
  reports those before it initialises and mid-drag, and `formatISO` throws on nil."
  [master-id {:keys [started-at ended-at preset]}]
  (when (and started-at ended-at)
    (dispatch [:dependency-graph.timeline.events/set-timeline
               {:master-id  master-id
                :started-at (formatISO started-at)
                :ended-at   (formatISO ended-at)
                :preset     preset}])))

(defn set-subwindow!
  "Writes the sub-selection to the db. A partial or nil window clears it."
  [master-id {:keys [started-at ended-at]}]
  (dispatch [:dependency-graph.timeline.events/set-subtimeline
             (cond-> {:master-id master-id}
               (and started-at ended-at)
               (assoc :started-at (formatISO started-at)
                      :ended-at   (formatISO ended-at)))]))

;; Debounced for the gantt's drag. Zoom/pan are events instead — a debounce
;; drops intermediate calls, collapsing repeated clicks into one step.
(def set-timeline (debounce set-window! 200))
(def set-subtimeline (debounce set-subwindow! 200))

;; Debounced here rather than at the state, so db reflects each click at once.
(def ^:private fetch-executions
  (debounce #(dispatch [:dependency-graph.timeline.api/fetch-executions %]) 200))

(def ^:private fetch-executions-log
  (debounce #(dispatch [:dependency-graph.timeline.api/fetch-executions-log %]) 200))


;; --- periods ---------------------------------------------------------------

(def periods
  [{:id :last-hour :label "Last hour"
    :window #(hours-ending-now % 1)}
   {:id :last-6-hours :label "Last 6 hours"
    :window #(hours-ending-now % 6)}
   {:id :last-24-hours :label "Last 24 hours"
    :window #(hours-ending-now % 24)}
   {:id :today :label "Today"
    :window (fn [now] {:started-at (startOfDay now) :ended-at (endOfDay now)})}
   {:id :last-7-days :label "Last 7 days"
    :window #(hours-ending-now % (* 7 24))}])

(def ^:private period-labels
  (into {:custom "Custom range"} (map (juxt :id :label) periods)))

(defn- contains-now? [{:keys [started-at ended-at]} now]
  (boolean (and started-at ended-at now
                (not (isAfter started-at now))
                (not (isBefore ended-at now)))))


;; --- restoring a window from the URL ---------------------------------------
;; Read from `location`, not the route sub: this pane writes the query on every
;; pan, and subscribing would feed each write back as a re-render.

(defn- query-param [name]
  (let [v (.get (js/URLSearchParams. js/window.location.search) name)]
    (when-not (str/blank? v) v)))

(defn- iso-window
  "The window named by two query parameters, or nil if either is missing or is not
   a date — an Invalid Date in the db would throw in every formatter downstream."
  [from-param to-param]
  (let [from (query-param from-param)
        to   (query-param to-param)]
    (when (and from to
               (valid-date? (parseISO from))
               (valid-date? (parseISO to)))
      {:started-at from :ended-at to})))

(defn url-window []
  (iso-window "tl-from" "tl-to"))

(defn url-subwindow []
  (iso-window "tl-sub-from" "tl-sub-to"))

;; --- controls --------------------------------------------------------------

(defn period-menu
  "The date-range picker, with the common windows listed inside it. One control:
   open it and either take a preset or pick dates, rather than a preset menu and
   a calendar sitting side by side arguing about which one owns the window."
  [{:keys [master-id preset timezone timeline]}]
  (let [zoned-now #(toZonedTime (js/Date.) timezone)]
    [datetime-picker
     {:resetable false
      :compact?  true
      ;; `[]`, never nil: a nil value puts null into the picker's state and crashes it.
      :value     (if timeline [(:started-at timeline) (:ended-at timeline)] [])
      :presets   (mapv (fn [{:keys [id label window]}]
                         {:label     label
                          :active?   (= id preset)
                          :on-select #(set-window! master-id (assoc (window (zoned-now)) :preset id))})
                       periods)
      :on-change (fn [[from to]]
                   (when (and from to)
                     (set-window! master-id {:started-at from
                                             :ended-at   to
                                             :preset     :custom})))}]))

;; Live refetches the whole history every 10s, and on a large pipeline that
;; response is big enough to take the page down. Off — button and default — until
;; kioku sends a response proportional to the runs rather than the pipeline.
(def ^:private live-available? false)

;; Shared by `controls` and `run-strip`, so swapping them does not move the pane.
(def ^:private toolbar-class
  "flex items-center gap-0.5 h-9 px-2 border-b border-kleene-layout-border/50 text-xs")

(defn- toolbar-divider []
  [:div {:class "shrink-0 w-px h-4 mx-1 bg-kleene-layout-border"}])

(defn- tool [props]
  [button (merge {:variant :empty-transparent :size :icon-sm} props)])

(defn run-strip
  "Replaces `controls` while a run is zoomed onto."
  [{:keys [master-id summary scheduled labels]}]
  [:div {:class (clsx toolbar-class "justify-between gap-3 pl-3 bg-kleene-selected-background/30")}
   [:div {:class "flex items-center gap-2 min-w-0 overflow-hidden whitespace-nowrap text-kleene-text-secondary"
          :title (when-let [{:keys [master-id ms]} (:slowest summary)]
                   (str "Slowest: " (or (get labels master-id) "Transform") " · "
                        (short-duration ms #js {:round true})))}
    [:span {:class "font-medium text-kleene-text"}
     "Run " (safe-format scheduled "d MMM HH:mm")]
    ;; nil while a refetch of the history is in flight
    (when-let [{:keys [transforms failed running span-ms]} summary]
      [:<>
       [:span "·"]
       [:span (str transforms " transform" (when (not= 1 transforms) "s"))]
       (when (pos? failed)
         [:<> [:span "·"] [:span {:class "text-kleene-text-error"} (str failed " failed")]])
       (when (pos? running)
         [:<> [:span "·"] [:span {:class "text-kleene-text-warning"} (str running " running")]])
       [:span "·"]
       [:span (short-duration span-ms #js {:round true}) (when (pos? running) " so far")]])]
   [tool {:icon       X
          :title      "Close the run and go back"
          :aria-label "Close the run and go back"
          :on-click   #(dispatch [:dependency-graph.timeline.events/back {:master-id master-id}])}]])

(defn controls
  [{:keys [master-id mode set-mode subtimeline set-subtimeline refresh timeline
           preset timezone live? loading?]}]
  (let [at-floor? (and timeline (timeline.events/at-min-zoom? timeline))
        at-ceil?  (and timeline (timeline.events/at-max-zoom? timeline))
        arming?   (and (= mode :left-handle) (empty? subtimeline))]
    [:div {:class toolbar-class}
     [period-menu {:master-id master-id
                   :preset    preset
                   :timezone  timezone
                   :timeline  timeline}]
     [:span {:class "min-w-0 truncate px-1 text-kleene-text-secondary"
             :title (str "All times in this pane are shown in " timezone)}
      (when-let [active (get period-labels preset)]
        (str active " · "))
      timezone]
     [tooltip {:content [status/status-legend {:class "flex-col items-start gap-1"}]}
      [:span {:class "shrink-0 p-1 text-kleene-text-secondary cursor-help"
              :aria-label "Status colour key"}
       [:> Info {:size 14}]]]

     [:div {:class "grow"}]

     (when live-available?
       [tooltip {:content (if live?
                            "Auto-refreshing every 10s. Click to stop."
                            "Refresh automatically while this window includes now")}
        [button {:variant  (if live? :secondary :empty-transparent)
                 :size     :micro
                 :icon     Activity
                 :class    (clsx "h-6" {"text-kleene-text-success" live?})
                 :on-click #(dispatch [:dependency-graph.timeline.events/toggle-live
                                       {:master-id master-id :live? (not live?)}])}
         "Live"]])

     [tooltip {:content "Select a time range on the timeline"}
      [button {:variant  (if subtimeline :secondary :empty-transparent)
               :size     :micro
               :icon     FunnelIcon
               :class    (clsx "h-6 shrink-0 whitespace-nowrap"
                               {"bg-kleene-secondary-01 dark:bg-kleene-secondary-v2 text-white" arming?})
               :on-click (fn []
                           (set-subtimeline nil)
                           (set-mode :left-handle))}
       (if subtimeline
         (window-text timezone subtimeline)
         "Select range")]]
     (when subtimeline
       [tool {:icon     FunnelXIcon
              :title    "Clear the selected range"
              :on-click #(set-subtimeline nil)}])

     [toolbar-divider]

     (when refresh
       [tool {:title      "Refresh data"
              :aria-label "Refresh data"
              :loading    loading?
              :icon       RefreshCcw
              :on-click   refresh}])

     [toolbar-divider]

     [tool {:icon       ZoomInIcon
            :disabled   at-floor?
            :title      (if at-floor? "Zoom in stops at 1 hour — click a run's line to zoom closer" "Zoom in")
            :aria-label "Zoom in"
            :on-click   #(dispatch [:dependency-graph.timeline.events/zoom {:master-id master-id :direction :in}])}]
     [tool {:icon       ZoomOutIcon
            :disabled   at-ceil?
            :title      (if at-ceil? "Already at the largest window the API serves (366 days)" "Zoom out")
            :aria-label "Zoom out"
            :on-click   #(dispatch [:dependency-graph.timeline.events/zoom {:master-id master-id :direction :out}])}]
     [tool {:icon       ChevronLeftIcon
            :title      "Earlier (half a window)"
            :aria-label "Pan to earlier runs"
            :on-click   #(dispatch [:dependency-graph.timeline.events/pan {:master-id master-id :direction :back}])}]
     [tool {:icon       ChevronRightIcon
            :title      "Later (half a window)"
            :aria-label "Pan to later runs"
            :on-click   #(dispatch [:dependency-graph.timeline.events/pan {:master-id master-id :direction :forward}])}]]))


;; --- executions table ------------------------------------------------------

(defn- duration-ms
  "Run length in ms, or nil. Explicitly nil rather than NaN for an unusable pair:
   NaN is truthy in CLJS, so it would sail through every `when`/`cond` guarding a
   duration and reach the formatter as a length."
  [{:keys [started-at finished-at]}]
  (when (and (valid-date? started-at) (valid-date? finished-at))
    (let [ms (differenceInMilliseconds finished-at started-at)]
      (when-not (js/isNaN ms) ms))))

(defn row-actions
  "Per-row triage actions."
  [{:keys [execution on-view-logs can-view-logs? on-open-transform on-zoom-run on-focus]}]
  [dropdown-menu/dropdown-menu-root
   [dropdown-menu/dropdown-menu-trigger {:as-child false}
    [button {:variant    :empty-transparent
             :size       :icon-sm
             :icon       EllipsisVertical
             :title      "Actions"
             :aria-label (str "Actions for " (:name execution))
             :on-click   (fn [e] (.stopPropagation e))}]]
   [dropdown-menu/dropdown-menu-content
    ;; Portaled by Radix, so a click in here never reaches the row underneath.
    (when on-focus
      [dropdown-menu/dropdown-menu-item
       {:class "gap-2" :on-click #(on-focus (:master-id execution))}
       [:> Crosshair {:size 14}] "Focus on graph"])
    [dropdown-menu/dropdown-menu-item
     {:class    "gap-2"
      :disabled (nil? (:scheduled-at execution))
      :on-click #(on-zoom-run (:scheduled-at execution))}
     [:> ZoomInIcon {:size 14}] "Zoom to its run"]
    (when on-view-logs
      [dropdown-menu/dropdown-menu-item
       {:class    "gap-2"
        :disabled (not can-view-logs?)
        :title    (when-not can-view-logs?
                    "You do not have permission to read logs")
        :on-click #(on-view-logs execution)}
       [:> ScrollText {:size 14}] "View logs"])
    (when on-open-transform
      [dropdown-menu/dropdown-menu-item
       {:class "gap-2" :on-click #(on-open-transform execution)}
       [:> Crosshair {:size 14}] "Open transform"])
    [dropdown-menu/dropdown-menu-item
     {:class    "gap-2"
      :disabled (str/blank? (:error execution))
      :on-click #(dispatch [:app.events/copy-to-clipboard (:error execution) true])}
     [:> Copy {:size 14}] "Copy error"]]])

(defn- cell-date
  "A date cell, or an em dash. An empty cell used to still carry the three-zone
   date tooltip, which opened on hover showing three blank rows."
  [{:keys [date run timezone]}]
  (if date
    [date-tooltip {:date       date
                   :seconds?   true
                   :time-only? (same-day? timezone run date)}]
    [:span {:class "text-kleene-text-secondary"} "—"]))

(defn table-row
  "Every row must render at one height — the virtualised table measures one and
   stands spacers in for the rest. Hence the group line reserved on every row
   once any has a group, and the error pinned to a single line: keep any new
   cell from wrapping."
  [{:keys [execution group group-row? timezone now selected? actions on-row-hover on-row-click
           on-run-click]}]
  ;; Run carries the date for the row; a start/finish on that same date shows
  ;; only its time, and re-grows the date on the runs that cross midnight.
  (let [tz       (or timezone (get-browser-timezone))
        run      (:scheduled-at execution)
        normal   (normalize-status (:status execution))
        running? (= normal :running)
        select   #(on-row-click execution)]
    [table/tr {:id             (str "exec-row-" (:execution-id execution))
               :data-exec-row  true
               :tab-index      0
               :on-click       select
               :on-key-down    (fn [e]
                                 (when (contains? #{"Enter" " "} (.-key e))
                                   (.preventDefault e)
                                   (select)))
               :on-mouse-enter #(on-row-hover execution)
               :on-mouse-leave #(on-row-hover nil)
               :class (clsx "h-7 group hover:*:bg-kleene-hover-background *:transition-colors cursor-pointer"
                            "has-[.error-message:hover]:*:bg-transparent"
                            "focus-visible:outline-1 focus-visible:outline-kleene-text-link"
                            {"*:bg-kleene-selected-background/40" selected?})}
     ;; Name over group. `group-row?` reserves the group line on every row, so
     ;; heights stay level.
     [table/td {:title (str/join " · " (remove str/blank? [(:name execution) group]))
                :class "overflow-hidden"}
      [:div {:class "flex flex-col overflow-hidden"}
       ;; tighter than `leading-4` clips Poppins' descenders
       [:span {:class "truncate leading-4"} (:name execution)]
       (when group-row?
         [:span {:class "truncate h-3 text-[10px] leading-3 text-kleene-text-secondary"} group])]]
     ;; Total mapping — the log endpoint returns in-flight rows too.
     [table/td [status/status-pill normal]]
     ;; The fire-time; clicking it zooms to the run, as its tick does.
     [table/td (cond-> {}
                 run (assoc :class    "cursor-pointer"
                            :on-click (fn [e]
                                        (.stopPropagation e)
                                        (on-run-click run))))
      (if run
        [date-tooltip {:date run :class "cursor-pointer hover:underline"}]
        [:span {:class "text-kleene-text-secondary"} "—"])]
     [table/td [cell-date {:date (:started-at execution) :run run :timezone tz}]]
     [table/td [cell-date {:date (:finished-at execution) :run run :timezone tz}]]
     [table/td
      (cond
        (duration-ms execution) (format-execution-time execution)
        (and running? (valid-date? (:started-at execution)) (valid-date? now))
        [:span {:class "text-kleene-text-warning"}
         (str (short-duration (differenceInMilliseconds now (:started-at execution))
                              #js {:round true})
              " so far")]
        :else [:span {:class "text-kleene-text-secondary"} "—"])]
     [table/td {:class "overflow-hidden"}
      (when-not (str/blank? (:error execution))
        [popover/popover-root
         ;; Cancels this trigger's own padding, and pins the monospace line box to the
         ;; height of every other cell, so an error row is not taller than a plain one.
         [popover/popover-trigger {:class "inline-block overflow-hidden w-full hover:bg-kleene-hover-background -mx-1 px-1 rounded text-left"
                                   :on-click (fn [e] (.stopPropagation e))}
          [:pre {:class "error-message whitespace-nowrap overflow-hidden text-ellipsis text-xs leading-4"}
           [:code (:error execution)]]]
         [popover/popover-content {:class "max-w-5xl overflow-auto max-h-radix-popover-content-available"}
          [:div {:class "flex justify-end mb-2"}
           [button {:variant  :secondary
                    :size     :small
                    :icon     Copy
                    :on-click #(dispatch [:app.events/copy-to-clipboard (:error execution) true])}
            "Copy"]]
          [:pre {:class "error-message"}
           [:code (:error execution)]]]])]
     [table/td {:class "text-right" :no-padding true}
      [row-actions (assoc actions :execution execution)]]]))

(def ^:private column-widths-key "execution-timeline-log-columns")

;; Error is deliberately absent — it is the flexible column, so it has no handle.
(def ^:private default-column-widths
  {:name 384 :status 96 :run 192 :started-at 192 :finished-at 192 :duration 112 :actions 44})

;; Not sortable: the log reads in the order kioku returns the runs in.
(def ^:private columns
  [[:name "Name"]
   [:status "Status"]
   [:run "Run"]
   [:started-at "Started at"]
   [:finished-at "Finished at"]
   [:duration "Duration"]])

(defn- skeleton-rows [n]
  (for [i (range n)]
    [table/tr {:key i}
     (for [c (range 8)]
       [table/td {:key c} [skeleton {:height 10}]])]))

(def ^:private overscan-rows 20)

(defn- use-virtual-rows
  "The slice of `total` rows worth rendering: those on screen in the element
   behind `scroller-ref`, plus `overscan-rows` either side. A wide window on a
   large pipeline returns tens of thousands of runs, and a DOM row for each is
   what takes the page down.

   Rows are all one height — measured off a rendered row — so the rows outside
   the slice are stood in for by a spacer above and below (`:top` / `:bottom`,
   px). The offset ignores the header, which the overscan absorbs."
  [scroller-ref total]
  (let [[first-row set-first-row] (react/useState 0)
        [viewport set-viewport]   (react/useState 800)
        [row-h set-row-h]         (react/useState 29)]
    (react/useEffect
     (fn []
       (if-let [el (.-current scroller-ref)]
         (let [observer (js/ResizeObserver. #(set-viewport (.-clientHeight el)))]
           (.observe observer el)
           #(.disconnect observer))
         js/undefined))
     #js [])
    ;; Before paint, so a wrong estimate never shows as a jump. Also re-reads the
    ;; scroll offset: when the rows shrink while scrolled down (a narrower
    ;; subtimeline, say), the browser clamps scrollTop without a scroll event
    ;; having reached `first-row` yet, and the slice would sit past the end.
    (react/useLayoutEffect
     (fn []
       (when-let [el (.-current scroller-ref)]
         (when-let [row (.querySelector el "tr[data-exec-row]")]
           (let [h (.-offsetHeight row)]
             (when (and (pos? h) (not= h row-h))
               (set-row-h h))))
         (set-first-row (js/Math.floor (/ (.-scrollTop el) row-h))))
       js/undefined))
    ;; `start` clamped as well as `end`: `first-row` can still point into a longer,
    ;; previous list on the render the shorter one arrives in, and `subvec` throws
    ;; past the end.
    (let [start (min total (max 0 (- first-row overscan-rows)))
          end   (min total (+ first-row (js/Math.ceil (/ viewport row-h)) overscan-rows))]
      {:start     start
       :end       (max start end)
       :row-h     row-h
       :top       (* start row-h)
       :bottom    (* (max 0 (- total end)) row-h)
       ;; Only a change of first row re-renders: React drops an equal state.
       :on-scroll #(set-first-row (js/Math.floor (/ (.. % -currentTarget -scrollTop) row-h)))})))

(defn- spacer-row
  "Stands in for the rows `use-virtual-rows` leaves out."
  [height]
  (when (pos? height)
    [:tr {:aria-hidden true}
     [:td {:col-span 8 :style {:height height :padding 0 :border 0}}]]))

(defn- scroll-row-into-view!
  "Scrolls `el` just far enough to show row `i` in full below the sticky header —
   `scrollIntoView`'s `nearest`, by index, since the row may not be rendered."
  [^js el i row-h]
  (let [header      (or (some-> (.querySelector el "thead") .-offsetHeight) 0)
        row-top     (+ header (* i row-h))
        view-top    (+ (.-scrollTop el) header)
        view-bottom (+ (.-scrollTop el) (.-clientHeight el))]
    (cond
      (< row-top view-top)
      (set! (.-scrollTop el) (- row-top header))

      (> (+ row-top row-h) view-bottom)
      (set! (.-scrollTop el) (- (+ row-top row-h) (.-clientHeight el))))))

(defn executions-table
  "Rendered as `[:f> executions-table …]`: it uses hooks. Virtualised: only the
   rows on screen are rendered (see `use-virtual-rows`)."
  [{:keys [data group-labels timezone now loading? error selected-id actions on-row-hover
           on-row-click on-run-click on-retry on-widen]}]
  ;; `useState`, not `useMemo`: React may discard a memo, and a discard mid-drag
  ;; would lose the widths.
  (let [[widths set-widths] (react/useState #(column-widths column-widths-key
                                                            default-column-widths))
        resizable {:widths widths :storage-key column-widths-key :on-resize set-widths}
        ;; Rows stay on screen through a refetch. `nil` data is a window that has not
        ;; come back yet, including during the fetch debounce.
        first-load? (or (nil? data) (and loading? (empty? data)))
        rows        (vec data)
        scroller    (react/useRef nil)
        ;; The selection last brought into view, so a refetch does not drag the
        ;; reader back to it after they have scrolled away.
        scrolled-to (react/useRef nil)
        {:keys [start end top bottom row-h on-scroll]} (use-virtual-rows scroller (count rows))]
    ;; A run picked on the gantt is most likely off screen, so not rendered — scroll
    ;; to it by index rather than by looking its row up. On `rows` too: a run picked
    ;; while its window's log is still loading is scrolled to once it lands.
    (react/useEffect
     (fn []
       (when-let [el (and selected-id
                          (not= selected-id (.-current scrolled-to))
                          (.-current scroller))]
         (when-let [i (first (keep-indexed #(when (= selected-id (:execution-id %2)) %1) rows))]
           (scroll-row-into-view! el i row-h)
           (set! (.-current scrolled-to) selected-id)))
       js/undefined)
     #js [selected-id rows])
    [:div {:class     "relative overflow-auto h-full"
           :ref       scroller
           :on-scroll on-scroll}
     (when (and loading? (not first-load?))
       [:div {:class "sticky top-0 left-0 h-0.5 z-30 bg-kleene-text-link/70 animate-kleene-pulse"
              :role  "progressbar"
              :aria-label "Refreshing executions"}])
     ;; `:sticky-header` for `border-separate`, and `:sticky` puts the offset on the
     ;; *cells*: sticky on a `tr` is a no-op outside Firefox, and `border-collapse`
     ;; defeats it on cells too. Same pair as `minorodata.components.log.table`.
     ;; denser than the table defaults: this pane gets a few hundred rows
     [table/table {:sticky-header true
                   :class (clsx "bg-kleene-background border table-fixed text-xs"
                                "[&_td]:px-2 [&_td]:py-0.5 [&_th]:px-2 [&_th]:py-1"
                                {"opacity-60" (and loading? (not first-load?))})}
      [table/thead
       [table/tr {:sticky true :class "h-7 *:top-0!"}
        (for [[id label] columns]
          ^{:key (name id)}
          [table/resizable-th (assoc resizable :id id) label])
        ;; No width of its own, so it takes whatever the resizable columns
        ;; leave — down to a floor, past which the table scrolls instead.
        [table/th {:class "min-w-[12rem]"} "Error"]
        [table/th {:style {:width (:actions default-column-widths)}
                   :aria-label "Actions"}]]]
      [table/tbody
       (cond
         error
         [table/tr
          [table/td {:col-span 8}
           [alert {:variant :error :title "Could not load this window"}
            [:div {:class "flex flex-col items-start gap-2"}
             [:span error]
             [button {:variant :secondary :size :small :icon RefreshCcw :on-click on-retry}
              "Try again"]]]]]

         first-load?
         (skeleton-rows 6)

         (empty? data)
         [table/tr
          [table/td {:col-span 8 :class "text-center py-8"}
           [:div {:class "flex flex-col items-center gap-2"}
            [:span {:class "text-kleene-text-secondary"} "No runs in this window."]
            [button {:variant :secondary :size :small :on-click on-widen}
             "Show last 7 days"]]]]

         :else
         [:<>
          [spacer-row top]
          (for [execution (subvec rows start end)]
            [table-row {:key          (:execution-id execution)
                        :execution    execution
                        :group        (get group-labels (:master-id execution))
                        :group-row?   (boolean (seq group-labels))
                        :timezone     timezone
                        :now          now
                        :selected?    (= selected-id (:execution-id execution))
                        :actions      actions
                        :on-row-hover on-row-hover
                        :on-row-click on-row-click
                        :on-run-click on-run-click}])
          [spacer-row bottom]])]]]))


;; --- pane ------------------------------------------------------------------

(def ^:private live-refresh-ms 10000)

(defn- use-clock
  "Re-renders once a second while `on?`, so \"now\" — the marker line and the
   elapsed time on a running row — stays true between refetches."
  [on?]
  (let [[tick set-tick] (react/useState 0)]
    (react/useEffect
     (fn []
       (if on?
         (let [id (js/setInterval #(set-tick inc) 1000)]
           #(js/clearInterval id))
         js/undefined))
     #js [on?])
    tick))

(defn- use-value-memo
  "`useMemo` keyed on value (`=`) rather than identity. The hosts rebuild their
   lookup maps (`labels`, `master->name`) on every render, so an identity-keyed
   memo was thrown away by each loading-flag flip; `=` on an unchanged map
   is far cheaper than what it guards."
  [f deps]
  (let [cache (react/useRef nil)
        [cached-deps cached] (.-current cache)]
    (if (and (.-current cache) (= cached-deps deps))
      cached
      (let [v (f)]
        (set! (.-current cache) [deps v])
        v))))

(defn- run-tooltip [labels e]
  (str (or (get labels (:master-id e)) "Transform") "\n"
       (status/status-label (normalize-status (:status e))) "\n"
       (when-let [t (safe-format (:started-at e) "d MMM HH:mm:ss")]
         (str "Started " t "\n"))
       (if-let [ms (duration-ms e)]
         (str "Took " (short-duration ms #js {:round true}))
         "Still running")))

(defn- gantt-rows
  "One gantt row per transform in `transforms` — kioku's row order, upstream
   first — holding its runs. A transform that never ran keeps its (empty) row,
   but with no runs at all there are no rows, so the gantt shows its empty state."
  [transforms data labels]
  (if (empty? data)
    []
    (let [by-id (group-by :master-id data)]
      (mapv (fn [id]
              {:id           id
               :transform-id id
               :label        (get labels id)
               :executions   (into []
                                   (keep #(when (:started-at %)
                                            (assoc % :tooltip (run-tooltip labels %))))
                                   (get by-id id))})
            transforms))))

(defn execution-timeline'
  [{:keys [master-id transforms data logs schedules schedule-statuses timeline subtimeline timezone
           labels group-labels run
           preset live? history-error log-error loading-history? loading-log? url-sync?
           on-refresh on-schedule-click on-row-click on-row-hover on-view-logs
           can-view-logs? on-open-transform]}]
  (let [[hovered set-hovered] (react/useState nil)
        [hovered-master set-hovered-master] (react/useState nil)
        [selected set-selected] (react/useState nil)
        [mode set-mode] (react/useState :scroll) ;; :scroll | :left-handle | :right-handle

        timeline-key    (window-key timeline)
        subtimeline-key (window-key subtimeline)
        request-key     (history-key timezone timeline)

        ;; The clock only has something to advance while a run is in flight.
        running?        (boolean (some #(= :running (normalize-status (:status %))) logs))
        ticking?        (and live? running?)
        _tick           (use-clock ticking?)
        now             (toZonedTime (js/Date.) timezone)

        ;; The gantt marks the hovered execution; the host (if it wants it) marks
        ;; the transform's node on the graph.
        hover! (fn [execution]
                 (set-hovered (:execution-id execution))
                 (set-hovered-master (:master-id execution))
                 (when on-row-hover (on-row-hover (:master-id execution))))

        hover-row! (fn [id]
                     (set-hovered nil)
                     (set-hovered-master id)
                     (when on-row-hover (on-row-hover id)))

        ;; log rows carry true instants; `on-schedule-click` matches zoned ticks
        zoom-to-run! (fn [run]
                       (when (valid-date? run)
                         (on-schedule-click (toZonedTime run timezone))))

        ;; Selects the execution; the table scrolls its row into view (it may not
        ;; be rendered yet — the table only renders the rows on screen).
        select-execution! (fn [{:keys [execution-id] row-master-id :master-id}]
                            (set-selected execution-id)
                            (set-hovered execution-id)
                            (set-hovered-master row-master-id)
                            (when on-row-click (on-row-click row-master-id)))

        ;; The window the preset produces, so the control does not name one it is not.
        widen! (fn []
                 (set-window! master-id
                              (assoc (hours-ending-now now (* 7 24)) :preset :last-7-days)))

        ;; Memoised: hovering a bar re-renders this pane, and rebuilding every run's
        ;; tooltip each time is real work on a large pipeline. Both inputs only change
        ;; when the host re-renders with new data.
        grouped-data (use-value-memo #(gantt-rows transforms data labels) [transforms data labels])

        ;; Memoised: `data` can be tens of thousands of rows, and this pane
        ;; re-renders on every hover. The summary itself is left to tick with `now`.
        run-executions (use-value-memo
                        #(when run
                           (filterv (fn [e] (some-> (:scheduled-at e) .getTime (= (:scheduled run)))) data))
                        [data (:scheduled run)])
        summary        (when run (run-summary run-executions now))

        gantt-max (max 96 (content-height (count grouped-data) (boolean (seq schedules))))]

    (react/useEffect
     (fn []
       (when-not timeline
         (let [restored (when url-sync? (url-window))
               window   (or restored
                            {:started-at (-> now startOfDay formatISO)
                             :ended-at   (-> now endOfDay formatISO)})]
           (dispatch [:dependency-graph.timeline.events/init
                      {:master-id master-id
                       :timeline  window
                       :preset    (if restored :custom :today)
                       :live?     (and live-available?
                                       (contains-now? {:started-at (parseISO (:started-at window))
                                                       :ended-at   (parseISO (:ended-at window))}
                                                      now))}])
           (when url-sync?
             (when-let [sub (url-subwindow)]
               (dispatch [:dependency-graph.timeline.events/set-subtimeline (assoc sub :master-id master-id)])))))
       js/undefined)
     #js [master-id])

    ;; Keyed on the request, not the window: panning within a day is the same request.
    (react/useEffect
     (fn []
       (when (and master-id request-key)
         (fetch-executions (merge {:master-id master-id} timeline)))
       js/undefined)
     #js [master-id request-key])

    (react/useEffect
     (fn []
       (when (and master-id timeline-key)
         (fetch-executions-log (merge {:master-id master-id}
                                      (if subtimeline-key subtimeline timeline))))
       js/undefined)
     #js [master-id timeline-key subtimeline-key])

    ;; Only while the window includes now — and the history endpoint expands every
    ;; cron in the pipeline across the range, so this is not a cheap poll.
    (react/useEffect
     (fn []
       (if (and live? timeline (contains-now? timeline (toZonedTime (js/Date.) timezone)))
         (let [id (js/setInterval
                   (fn []
                     (when-not (or loading-history? loading-log?)
                       (on-refresh)))
                   live-refresh-ms)]
           #(js/clearInterval id))
         js/undefined))
     #js [live? timeline-key subtimeline-key loading-history? loading-log?])

    ;; Replaced rather than pushed: a pan is not a navigation. While a run is open
    ;; the URL keeps the window from before it — a reload restores no run bar, and
    ;; would strand the reader on a window a few seconds wide.
    (react/useEffect
     (fn []
       (when (and url-sync? timeline-key)
         (dispatch [:app.events/replace-query
                    (if-let [{prev :timeline prev-sub :subtimeline} (:previous run)]
                      {:tl-from     (:started-at prev)
                       :tl-to       (:ended-at prev)
                       :tl-sub-from (:started-at prev-sub)
                       :tl-sub-to   (:ended-at prev-sub)}
                      {:tl-from     (some-> timeline :started-at formatISO)
                       :tl-to       (some-> timeline :ended-at formatISO)
                       :tl-sub-from (some-> subtimeline :started-at formatISO)
                       :tl-sub-to   (some-> subtimeline :ended-at formatISO)})]))
       js/undefined)
     #js [url-sync? timeline-key subtimeline-key (:scheduled run)])

    [:div {:class "h-full grid grid-rows-[auto_1fr]"}
     (if run
       [run-strip {:master-id master-id
                   :summary   summary
                   :scheduled (some-> (:scheduled run) js/Date.)
                   :labels    labels}]
       [controls {:master-id       master-id
                  :timeline        timeline
                  :subtimeline     subtimeline
                  :preset          preset
                  :timezone        timezone
                  :live?           live?
                  :loading?        (or loading-history? loading-log?)
                  :mode            mode
                  :set-mode        set-mode
                  :refresh         on-refresh
                  :set-subtimeline (partial set-subtimeline master-id)}])

     [:> Allotment {:vertical true}
      [:> Allotment.Pane {:min-size       60
                          ;; no taller than its rows
                          :max-size       gantt-max
                          ;; Header plus 8 rows; more than this and the log table below shows one line.
                          :preferred-size (min 132 gantt-max)}
       [gantt {:timeline              timeline
               :mode                  mode
               :set-mode              set-mode
               :subtimeline           subtimeline
               :data                  grouped-data
               :anchor-id             master-id
               :now                   now
               :loading?              loading-history?
               :hovered-id            hovered
               :hovered-master-id     hovered-master
               :schedules             schedules
               :schedule-statuses     schedule-statuses
               :error-content         (when history-error
                                        [alert {:variant :error :title "Could not load runs"}
                                         [:div {:class "flex flex-col items-start gap-2 text-xs"}
                                          [:span history-error]
                                          [button {:variant :secondary :size :small
                                                   :icon RefreshCcw :on-click on-refresh}
                                           "Try again"]]])
               :empty-content         (when (some? data)
                                        [:div {:class "flex flex-col items-center gap-2 text-xs"}
                                         [:span {:class "text-kleene-text-secondary"}
                                          "No runs in this window."]
                                         [button {:variant :secondary :size :small :on-click widen!}
                                          "Show last 7 days"]])
               :on-bar-click          select-execution!
               :on-bar-hover          hover!
               :on-row-click          on-row-click
               :on-row-hover          hover-row!
               ;; a selected run fixes the window until it is closed
               :locked?               (some? run)
               :on-zoom               #(dispatch [:dependency-graph.timeline.events/zoom {:master-id master-id :direction %}])
               :on-pan                #(dispatch [:dependency-graph.timeline.events/pan {:master-id master-id :direction %}])
               :on-schedule-click     on-schedule-click
               :on-timeline-change    (partial set-timeline master-id)
               :on-subtimeline-change (partial set-subtimeline master-id)}]]

      [:f> executions-table {:data          logs
                         :group-labels  group-labels
                         :timezone      timezone
                         :now           now
                         :loading?      loading-log?
                         :error         log-error
                         :selected-id   selected
                         :actions       {:master-id         master-id
                                         :can-view-logs?    can-view-logs?
                                         :on-view-logs      on-view-logs
                                         :on-open-transform on-open-transform
                                         :on-zoom-run       zoom-to-run!
                                         :on-focus          on-row-click}
                         :on-run-click  zoom-to-run!
                         :on-retry      on-refresh
                         :on-widen      widen!
                         :on-row-hover  hover!
                         :on-row-click  (fn [execution]
                                          (set-selected (:execution-id execution))
                                          (when on-row-click
                                            (on-row-click (:master-id execution))))}]]]))

(defn timeline-page
  "`on-row-click` / `on-row-hover` are both given a row's master-id (hover gets
   nil on leave), so a host can focus or highlight the matching graph node.

   `master->name` maps a pipeline transform's master-id to its name — the gantt
   has a row per transform in the pipeline, and the API's rows carry ids only.
   `master->group` names the group each one lives in, shown under the name on its
   log row; omit it and the rows carry names alone, on a single line.
   `on-view-logs` / `on-open-transform` receive a log row; omit either and the
   action is left out of the row menu."
  [{:keys [master-id master->name master->group on-row-click on-row-hover on-view-logs
           can-view-logs? on-open-transform url-sync?]}]
  ;; Resolved once here: the sub is nil until the client request lands, and stays
  ;; nil for a client with no timezone set — and a nil zone makes date-fns-tz
  ;; return Invalid Date, which `.toISOString` throws on.
  (let [timezone       (or @(re-frame/subscribe [:app.subs/timezone])
                           (get-browser-timezone))
        timeline       @(re-frame/subscribe [:dependency-graph.timeline.subs/executions-timeline master-id])
        subtimeline    @(re-frame/subscribe [:dependency-graph.timeline.subs/executions-subtimeline master-id])
        transforms     @(re-frame/subscribe [:dependency-graph.timeline.subs/transforms master-id])
        executions     @(re-frame/subscribe [:dependency-graph.timeline.subs/executions master-id])
        execution-logs @(re-frame/subscribe [:dependency-graph.timeline.subs/execution-logs master-id])
        schedules      @(re-frame/subscribe [:dependency-graph.timeline.subs/execution-schedules master-id])
        schedule-statuses @(re-frame/subscribe [:dependency-graph.timeline.subs/execution-schedule-statuses master-id])
        run            @(re-frame/subscribe [:dependency-graph.timeline.subs/run master-id])
        preset         @(re-frame/subscribe [:dependency-graph.timeline.subs/preset master-id])
        live?          @(re-frame/subscribe [:dependency-graph.timeline.subs/live? master-id])
        history-error  @(re-frame/subscribe [:dependency-graph.timeline.subs/error master-id :history])
        log-error      @(re-frame/subscribe [:dependency-graph.timeline.subs/error master-id :log])
        loading-hist?  @(re-frame/subscribe [:mold-http/loading? :dependency-graph.timeline.api/fetch-executions])
        loading-log?   @(re-frame/subscribe [:mold-http/loading? :dependency-graph.timeline.api/fetch-executions-log])

        ;; From the host's graph, falling back to the name on a log row.
        labels         (merge (into {} (keep (fn [{:keys [master-id name]}]
                                               (when (and master-id name) [master-id name]))
                                             execution-logs))
                              master->name)

        on-schedule-click (fn [timestamp]
                            ;; `now` zoned, like every other date here.
                            (when-let [{:keys [started-at ended-at]}
                                       (run-window (filter #(isEqual (:scheduled-at %) timestamp) executions)
                                                   (toZonedTime (js/Date.) timezone))]
                              (dispatch [:dependency-graph.timeline.events/zoom-to-run
                                         {:master-id master-id
                                          :scheduled (.getTime timestamp)
                                          :window    {:started-at (formatISO started-at)
                                                      :ended-at   (formatISO ended-at)}}])))
        on-refresh        (fn []
                            (dispatch [:dependency-graph.timeline.api/fetch-executions
                                       (merge {:master-id master-id} timeline)])
                            (dispatch [:dependency-graph.timeline.api/fetch-executions-log
                                       (merge {:master-id master-id} (or subtimeline timeline))]))]

    [:f> execution-timeline' {:master-id         master-id
                              :transforms        transforms
                              :data              executions
                              :logs              execution-logs
                              :schedules         schedules
                              :schedule-statuses schedule-statuses
                              :run               run
                              :labels            labels
                              :group-labels      master->group
                              :timeline          timeline
                              :subtimeline       subtimeline
                              :timezone          timezone
                              :preset            preset
                              :live?             live?
                              :history-error     history-error
                              :log-error         log-error
                              :loading-history?  loading-hist?
                              :loading-log?      loading-log?
                              :url-sync?         url-sync?
                              :on-refresh        on-refresh
                              :on-schedule-click on-schedule-click
                              :on-row-click      on-row-click
                              :on-row-hover      on-row-hover
                              :on-view-logs      on-view-logs
                              :can-view-logs?    can-view-logs?
                              :on-open-transform on-open-transform}]))
